import type {Evaluator, Version} from "@cm/graph-v3"
import {versionChain} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {MaterialNode} from "#template/nodes/material/material-node"
import type {OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {MaterialLikeNode, NumberLike} from "#template/nodes/types"
import {materialLike} from "#template/nodes/material/fundamental"
import {numberLike} from "#template/nodes/number/fundamental"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {MaterialGraph} from "@cm/material-nodes"
import {transformOffsetUvs} from "@cm/material-nodes"
import type {UvOffset} from "#template/interfaces/scene-manager"
import {visitNone} from "#template/template-node/visit-helpers"

export type MoveMaterialParameters = {
    name: string
    material: MaterialLikeNode | null
    horizontalOffset?: NumberLike
    verticalOffset?: NumberLike
    rotation?: NumberLike
}

type V0 = {
    material: MaterialLikeNode | null
    horizontalOffset?: NumberLike
    verticalOffset?: NumberLike
    rotation?: NumberLike
}

type V1 = V0 & {name: string}

const v0: Version<V0, V1> = {
    toNextVersion: (parameters) => ({...parameters, name: "Move Material"}),
}

const moveMaterialDefinition: TemplateNodeDefinition<MoveMaterialParameters> = {
    nodeClass: "MoveMaterial",
    validation: {
        parameters: z.object({
            name: z.string(),
            material: z.nullable(materialLike),
            horizontalOffset: z.optional(numberLike),
            verticalOffset: z.optional(numberLike),
            rotation: z.optional(numberLike),
        }),
        returns: getTemplateNodeReturns(),
    },
    versionChain: versionChain([v0]),
}

@registerTemplateGraphNode
export class MoveMaterial extends MaterialNode<MoveMaterialParameters> {
    public override get definition() {
        return moveMaterialDefinition
    }

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MoveMaterialParameters> {
        if (this.parameters.material === null) return skipped
        return visitNone(this.parameters)
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<MaterialGraph | null> {
        const {horizontalOffset, verticalOffset, rotation} = this.parameters

        const material = evaluator.evaluate(this.parameters.material)
        let [materialValid, materialInvalid] = scope.branch(material)

        const nullToZero = (number: BuilderInlet<number | null>, uniqueId: string) => scope.pureLambda(number, (number) => number ?? 0, uniqueId)

        if (horizontalOffset || verticalOffset || rotation) {
            const offset = scope.struct<UvOffset>("UvOffset", {
                horizontal: nullToZero(evaluator.evaluate(horizontalOffset ?? 0), `horizontal`),
                vertical: nullToZero(evaluator.evaluate(verticalOffset ?? 0), `vertical`),
                rotation: nullToZero(evaluator.evaluate(rotation ?? 0), `rotation`),
            })

            materialValid = scope.pureLambda(
                scope.tuple(materialValid, offset),
                ([materialGraph, offset]) => transformOffsetUvs(materialGraph, offset),
                `transformOffsetUVs`,
            )
        }

        return scope.phi(materialValid, materialInvalid)
    }
}
